import { useMemo, useRef } from 'react';

import {
  Project,
  TaskSortOption,
  useListTaskTagsQuery,
} from '@graphql/generated';
import { TaskTagType } from '@src/types/task';

/**
 * TA-10502: batches the tag picker's per-project task fetch into a single
 * listTaskTags(projectIds: [...]) request instead of one request per visible
 * project row. Groups the combined response by projectId so each row can
 * still be rendered independently via ProjectTaskSection's staticTasks prop.
 *
 * `listTasks(projectIds: [...], first: N)` caps the COMBINED edge count
 * across every project in the connection, not N per project — with the
 * naive `first: firstPerProject` a handful of active projects could exhaust
 * the whole page and leave every other visible project with 0 tasks. To
 * compensate, the actual `first` sent to the server scales with the number
 * of projects requested (capped so the payload stays bounded), and the
 * grouped result is trimmed back down to `firstPerProject` per project so
 * downstream pagination-size assumptions (e.g. ProjectTaskSection's
 * "load more" heuristic) still hold. This narrows, but by construction of
 * a non-project-aware connection cannot fully eliminate, the starvation
 * risk — a project whose tasks all sort after the combined cutoff can still
 * come back empty.
 *
 * TA-10502 (QA fix): starvation therefore has to stay *recoverable* rather
 * than silent. `projectIdsWithMoreTasks` reports which projects may still
 * have tasks the batch did not return, so the row can keep offering its
 * per-project "load more" fallback instead of rendering a short group as if
 * it were complete. A project is flagged when the combined page came back
 * full (`edges.length >= first`, i.e. the connection was cut off, so *any*
 * project in it may have been starved) or when its own group had to be
 * trimmed to `firstPerProject`. When the page was not cut off the response
 * is the whole matching set, so short groups are genuinely complete and are
 * not flagged.
 *
 * `projectIds` grows every time the caller's own project list paginates
 * (e.g. "load more projects" while browsing). Each growth is a different
 * `projectIds` array, which is a different listTasks cache key — so the
 * connection has to be refetched from scratch and `data` goes briefly
 * `undefined` while it's in flight. Deriving `tasksByProjectId` purely from
 * the latest `data` made that read as a full-list flicker: every already-
 * rendered project's tasks vanished and reappeared on every page of
 * projects loaded. Grouping results are instead merged into an
 * accumulator ref keyed by projectId, so a project already resolved in an
 * earlier (smaller) batch keeps showing its tasks while a later, larger
 * batch is still in flight — only the newly-added projects show loading.
 *
 * That accumulator is only valid for a single set of query-shaping inputs.
 * `sortBy` / `excludeCompleted` / `firstPerProject` all change *which* tasks a
 * project should show, so groups resolved under the previous values are stale,
 * not merely older — the accumulator is keyed by those inputs and dropped
 * whenever they change. Only `projectIds` growth is additive.
 */

const DEFAULT_PER_PROJECT_COUNT = 10;
const MAX_BATCH_FIRST = 200;

export const useListTaskTagsBatchFromQuery = ({
  projectIds,
  sortBy = TaskSortOption.NameAsc,
  firstPerProject = DEFAULT_PER_PROJECT_COUNT,
  excludeCompleted = undefined,
  skip = false,
}: {
  projectIds: Project['id'][];
  sortBy?: TaskSortOption;
  firstPerProject?: number;
  excludeCompleted?: boolean;
  skip?: boolean;
}) => {
  const first = Math.min(
    firstPerProject * Math.max(projectIds.length, 1),
    MAX_BATCH_FIRST
  );

  const { data, loading } = useListTaskTagsQuery({
    variables: {
      projectIds,
      sortBy,
      first,
      excludeCompleted,
    },
    fetchPolicy: 'cache-and-network',
    skip: skip || projectIds.length === 0,
  });

  const accumulatorKey = `${sortBy}|${excludeCompleted}|${firstPerProject}`;
  // Joined rather than the array itself so the memo below doesn't recompute on
  // every render just because the caller built a fresh `projectIds` array.
  const projectIdsKey = projectIds.join(',');
  const accumulatedRef = useRef<{
    key: string;
    groups: Map<string, TaskTagType[]>;
    mayHaveMore: Set<string>;
  }>({ key: accumulatorKey, groups: new Map(), mayHaveMore: new Set() });

  const { tasksByProjectId, projectIdsWithMoreTasks } = useMemo(() => {
    if (accumulatedRef.current.key !== accumulatorKey) {
      accumulatedRef.current = {
        key: accumulatorKey,
        groups: new Map(),
        mayHaveMore: new Set(),
      };
    }
    if (!data?.listTasks?.edges) {
      return {
        tasksByProjectId: accumulatedRef.current.groups,
        projectIdsWithMoreTasks: accumulatedRef.current.mayHaveMore,
      };
    }

    const edges = data.listTasks.edges;
    // A full combined page means the connection was cut off, so no project in
    // this batch can be assumed complete.
    const isBatchTruncated = edges.length >= first;

    const freshGroups = new Map<string, TaskTagType[]>();
    const rawCounts = new Map<string, number>();
    for (const edge of edges) {
      const node = edge?.node;
      if (!node) continue;
      const task = node as unknown as TaskTagType;
      rawCounts.set(task.projectId, (rawCounts.get(task.projectId) ?? 0) + 1);
      const existing = freshGroups.get(task.projectId);
      if (existing) {
        if (existing.length < firstPerProject) existing.push(task);
      } else {
        freshGroups.set(task.projectId, [task]);
      }
    }

    const merged = new Map(accumulatedRef.current.groups);
    const mergedMayHaveMore = new Set(accumulatedRef.current.mayHaveMore);
    for (const projectId of projectIdsKey ? projectIdsKey.split(',') : []) {
      const fresh = freshGroups.get(projectId);
      if (fresh) {
        merged.set(projectId, fresh);
      } else if (!isBatchTruncated) {
        // The response covered the whole matching set and this project had no
        // tasks in it — evict any group left over from an earlier batch so a
        // project whose tasks were all deleted stops listing them.
        merged.set(projectId, []);
      }
      // When the page *was* cut off an absent project is "unknown", not
      // "empty": keep whatever it last resolved to and let the row offer
      // "load more" instead of asserting emptiness.
      if (
        isBatchTruncated ||
        (rawCounts.get(projectId) ?? 0) > firstPerProject
      ) {
        mergedMayHaveMore.add(projectId);
      } else {
        mergedMayHaveMore.delete(projectId);
      }
    }
    accumulatedRef.current = {
      key: accumulatorKey,
      groups: merged,
      mayHaveMore: mergedMayHaveMore,
    };
    return {
      tasksByProjectId: merged,
      projectIdsWithMoreTasks: mergedMayHaveMore,
    };
  }, [data, first, firstPerProject, accumulatorKey, projectIdsKey]);

  return {
    tasksByProjectId,
    projectIdsWithMoreTasks,
    loading: skip ? false : loading,
  };
};
